import { INetsuiteSyncStatusDTO } from '../common/types';

export interface IProviderDTO {
	id: string;
	fantasyName: string;
	businessName: string;
	country: {
		code: string;
		name: string;
	};
	stateName: string;
	cityName: string;
	street: string;
	postalCode: string;
	rut: string;
	phone: string;
	email: string;
	enable: boolean;
}

export interface IProviderBulkInsertInNetsuiteResultDTO {
	totalToInsert: number;
	totalMinutesToEndInsert: number;
}

export interface IProviderPage {
	page: number;
	size: number;
	total: number;
	sort?: string;
	elements: IProviderListDTO[];
}

export interface IProviderListDTO {
	id: string;
	fantasyName: string;
	businessName: string;
	country: {
		code: string;
		name: string;
	};
	stateName: string;
	cityName: string;
	street?: string;
	postalCode?: string;
	rut?: string;
	phone?: string;
	email?: string;
	enable: boolean;
	hasPaymentDateRule: boolean;
	syncStatus: INetsuiteSyncStatusDTO;
}

export interface IPatchProviderForm {
	id: string;
	fantasyName: string;
	businessName: string;
	country: string;
	stateName: string;
	cityName: string;
	street?: string;
	postalCode?: string;
	rut?: string;
	phone?: string;
	email?: string;
	enable: string;
}

// providerPaymentDateRule.types.ts

export enum StrategyCode {
	NetDays = 'NetDays',
	SplitMonth = 'SplitMonth',
	MonthlyRelative = 'MonthlyRelative',
	WeeklyRelative = 'WeeklyRelative',
	CustomCalendar = 'CustomCalendar',
}

export enum TriggerSource {
	ServiceCreation = 'ServiceCreation',
	CheckIn = 'CheckIn',
	CheckOut = 'CheckOut',
}

export enum DayOfWeekEnum {
	Monday = 'Monday',
	Tuesday = 'Tuesday',
	Wednesday = 'Wednesday',
	Thursday = 'Thursday',
	Friday = 'Friday',
	Saturday = 'Saturday',
	Sunday = 'Sunday',
}

export type PeriodRule = {
	monthOffset: number;
	fixedDay: number;
};

export type CalendarPeriod = {
	from: string; // YYYY-MM-DD
	to: string; // YYYY-MM-DD
	dueDate: string; // YYYY-MM-DD
};

/** FallbackStrategy NO lleva triggerSource (hereda del padre). */
export type FallbackStrategy =
	| { strategyCode: StrategyCode.NetDays; days: number }
	| {
			strategyCode: StrategyCode.MonthlyRelative;
			monthOffset: number;
			dayOfWeek: DayOfWeekEnum;
			weekOrdinal: number; // -1 o 1..5
	  }
	| {
			strategyCode: StrategyCode.SplitMonth;
			cutoffDay: number;
			firstPeriod: PeriodRule;
			secondPeriod: PeriodRule;
	  }
	| {
			strategyCode: StrategyCode.WeeklyRelative;
			weeksOffset: number;
			dayOfWeek: DayOfWeekEnum;
	  };

export type NetDaysConfig = {
	triggerSource: TriggerSource;
	days: number; // >= 0
};

export type MonthlyRelativeConfig = {
	triggerSource: TriggerSource;
	monthOffset: number; // >= 0
	dayOfWeek: DayOfWeekEnum;
	weekOrdinal: number; // -1 o 1..5
};

export type WeeklyRelativeConfig = {
	triggerSource: TriggerSource;
	weeksOffset: number; // >= 0
	dayOfWeek: DayOfWeekEnum;
};

export type SplitMonthConfig = {
	triggerSource: TriggerSource;
	cutoffDay: number; // 1..31
	firstPeriod: PeriodRule;
	secondPeriod: PeriodRule;
};

export type CustomCalendarConfig = {
	triggerSource: TriggerSource;
	periods: CalendarPeriod[]; // non-empty
	fallbackStrategy: FallbackStrategy; // obligatorio
};

export type PaymentDateRuleConfig =
	| NetDaysConfig
	| MonthlyRelativeConfig
	| WeeklyRelativeConfig
	| SplitMonthConfig
	| CustomCalendarConfig;

export type ProviderPaymentDateRuleDTO = {
	strategyCode: StrategyCode;
	configuration: PaymentDateRuleConfig;
};

// Upsert que enviás al backend (igual al DTO)
export type ProviderPaymentDateRuleUpsertDTO = ProviderPaymentDateRuleDTO;

export interface IProviderPaymentCalendarGroupList {
	id: number;
	name: string;
	sortOrder: number;
}

export interface IProviderPaymentCalendarViewConfig {
	providerId: string;
	isGrouped: boolean;
	groupId?: number;
	groupIdName?: string;
	createdAt: string;
	createdById?: number;
	createdByName?: string;
	updatedAt?: string;
	updatedById?: number;
	updatedByName?: string;
}

export interface IProviderPaymentCalendarViewConfigUpdate {
	providerId: string;
	isGrouped: boolean;
	groupId?: number;
}

export type ProviderPaymentCalendarRowKeyDTO = { type: 'GROUP'; id: number } | { type: 'PROVIDER'; id: string };

export interface IProviderPaymentCalendarRowMetaDTO {
	key: ProviderPaymentCalendarRowKeyDTO;
	label: string;
	providerId?: string;
	businessName?: string;
	groupId?: number;
	groupName?: string;
	sortOrder: number;
}

export interface IProviderPaymentCalendarCellTotalDTO {
	rowKey: ProviderPaymentCalendarRowKeyDTO;
	paymentDate: string; // YYYY-MM-DD
	total: number;
}

export interface IProviderPaymentCalendarGridDTO {
	columnDates: string[]; // YYYY-MM-DD
	rowDefinitions: IProviderPaymentCalendarRowMetaDTO[];
	cellValues: IProviderPaymentCalendarCellTotalDTO[];
}

// filters que mandás al POST /grid
export interface IProviderPaymentCalendarFiltersDTO {
	from: string; // YYYY-MM-DD
	to: string; // YYYY-MM-DD (incluido)
	agencyId?: number;
	providerIds?: string[];
	expandedGroupIds?: number[];
}

export interface IProviderPaymentCalendarCellDetailRequestDTO {
	paymentDate: string; // YYYY-MM-DD
	agencyId?: number;
	groupId?: number;
	providerIds: string[];
}

export interface IProviderPaymentCalendarDetailItemDTO {
	providerId: string;
	providerFantasyName: string;
	providerBusinessName: string;
	saleOrderId: string;
	dealId: number;
	isBitrixDeal: boolean;
	serviceId: string;
	serviceReference: string;
	serviceType: string;
	paymentDate: string; // YYYY-MM-DD
	amount: number;
	currency: 'USD';
}
